import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { editUser as onEditUser } from "../../slices/thunks";
//formik
import { useFormik } from "formik";
import * as Yup from "yup";
import { userType } from "../../Components/constants/layout";
import { createSelector } from "reselect";

const FREE_TYPE_CUSTOM_REASON_MAX_LENGTH = 45;

const EditUser = ({ open, onClose, user }) => {
  const dispatch = useDispatch();
  
  // Determine initial values based on user prop
  const getInitialFreeTypeValues = () => {
    if (!user?.user_freeType) return { dropdown: '', custom: '' };
    const normalized = user.user_freeType.charAt(0).toUpperCase() + user.user_freeType.slice(1).toLowerCase();
    if (normalized === 'Bpa') {
      return { dropdown: 'BPA', custom: '' };
    }
    if (normalized === 'Team' || normalized === 'Trial') {
      return { dropdown: normalized, custom: '' };
    }
    return { dropdown: 'Other', custom: user.user_freeType };
  };
  
  const initialValues = getInitialFreeTypeValues();
  const [freeTypeDropdown, setFreeTypeDropdown] = useState(initialValues.dropdown);
  const [freeTypeCustomReason, setFreeTypeCustomReason] = useState(initialValues.custom);
  
  // Update local state when user prop changes
  useEffect(() => {
    const newValues = getInitialFreeTypeValues();
    setFreeTypeDropdown(newValues.dropdown);
    setFreeTypeCustomReason(newValues.custom);
  }, [user]);
  const selectUsersData = createSelector(
    (state) => state.Users,
    (editSuccess) => editSuccess
  );
  // Inside your component
  const { editSuccess } = useSelector(selectUsersData);

  useEffect(() => {
    if (editSuccess != null) {
      if (editSuccess) {
        validation.resetForm();
        setFreeTypeDropdown('');
        setFreeTypeCustomReason('');
        onClose();
      }
    }
  }, [editSuccess]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_email: user?.user_email || "",
      user_name: user?.user_name || "",
      user_sellerCloudCompanyId: user?.user_sellerCloudCompanyId ?? "",
      user_type: user?.user_type,
      user_notes: user?.user_notes || "",
      user_free: user?.user_free || false,
      user_freeType: user?.user_freeType || null,
      user_enabled: user?.user_enabled || false,
      user_showWarning: user?.user_showWarning || false,
      user_warningText: user?.user_warningText || "",
      user_billingCycleEnd: user?.user_billingCycleEnd
        ? new Date(user.user_billingCycleEnd).toISOString().slice(0, 10)
        : "",
    },
    validationSchema: Yup.object({
      user_email: Yup.string()
        .required("Please Enter a User Email")
        .email("Please Enter a Valid Email"),
      user_name: Yup.string().required("Please Enter a User Name"),
      user_sellerCloudCompanyId: Yup.number()
        .transform((value, originalValue) => originalValue === "" ? null : value)
        .nullable()
        .integer("Please Enter a Whole Number")
        .min(0, "Please Enter a Non-Negative Number")
        .max(2147483647, "Please Enter a Smaller Number"),
      user_type: Yup.number().required("Please Select a User Type"),
      user_notes: Yup.string(),
      user_free: Yup.boolean(),
      user_enabled: Yup.boolean(),
      user_showWarning: Yup.boolean(),
       user_warningText: Yup.string(),
      user_billingCycleEnd: Yup.string(),
    }),
    onSubmit: (values) => {
      // Validate freeType manually
      if (values.user_free && freeTypeDropdown === 'Other') {
        if (!freeTypeCustomReason.trim()) {
          validation.setFieldError('user_freeType', 'Please provide a reason for free access');
          return;
        }
        if (freeTypeCustomReason.length > FREE_TYPE_CUSTOM_REASON_MAX_LENGTH) {
          validation.setFieldError('user_freeType', `Free access reason cannot exceed ${FREE_TYPE_CUSTOM_REASON_MAX_LENGTH} characters`);
          return;
        }
      }
      
      const newUser = {
        ...user,
        user_email: values.user_email,
        user_name: values.user_name,
        user_sellerCloudCompanyId: values.user_sellerCloudCompanyId === ""
          ? null
          : Number(values.user_sellerCloudCompanyId),
        user_type: values.user_type,
        user_notes: values.user_notes,
        user_free: values.user_free,
        user_freeType: values.user_free 
          ? (freeTypeDropdown === 'Other' ? freeTypeCustomReason : (freeTypeDropdown || null))
          : null,
        user_enabled: values.user_enabled,
        user_showWarning: values.user_showWarning,
        user_warningText: values.user_warningText,
        user_billingCycleEnd: (values.user_free && freeTypeDropdown === 'Trial') && values.user_billingCycleEnd
          ? new Date(values.user_billingCycleEnd).toISOString()
          : user?.user_billingCycleEnd,
      };

      dispatch(onEditUser(newUser));
    },
  });

  const isTrial = validation.values.user_free && freeTypeDropdown === 'Trial';

  return (
    <Modal
      Modal
      isOpen={open}
      toggle={onClose}
      modalClassName="zoomIn"
      centered
      tabIndex="-1"
    >
      <ModalHeader toggle={onClose} className="p-3 bg-primary-subtle">
        <Row className="align-items-center gy-3">
          <div className="col-sm">
            <h5 className="card-title mb-0">Edit User</h5>
          </div>
        </Row>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  <Row>
                    <Col sm={12}>
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            name="user_enabled"
                            type="checkbox"
                            className="form-check-input"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            checked={validation.values.user_enabled || false}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="userEnabledSwitch"
                          >
                            This user is enabled
                          </label>
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Email</Label>
                        <Input
                          name="user_email"
                          type="text"
                          className={
                            validation.touched.user_email &&
                            validation.errors.user_email
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_email || ""}
                        />
                        {validation.touched.user_email &&
                        validation.errors.user_email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_email}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Name</Label>
                        <Input
                          name="user_name"
                          type="text"
                          className={
                            validation.touched.user_name &&
                            validation.errors.user_name
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_name || ""}
                        />
                        {validation.touched.user_name &&
                        validation.errors.user_name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_name}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="user_sellerCloudCompanyId">
                          SellerCloud Company ID
                        </Label>
                        <Input
                          id="user_sellerCloudCompanyId"
                          name="user_sellerCloudCompanyId"
                          type="number"
                          min="0"
                          step="1"
                          className={
                            validation.touched.user_sellerCloudCompanyId &&
                            validation.errors.user_sellerCloudCompanyId
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_sellerCloudCompanyId ?? ""}
                        />
                        {validation.touched.user_sellerCloudCompanyId &&
                        validation.errors.user_sellerCloudCompanyId ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_sellerCloudCompanyId}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Notes</Label>
                        <Input
                          name="user_notes"
                          type="textarea"
                          className={
                            validation.touched.user_notes &&
                            validation.errors.user_notes
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_notes || ""}
                        />
                        {validation.touched.user_notes &&
                        validation.errors.user_notes ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_notes}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">User Type</Label>
                        <Input
                          name="user_type"
                          type="select"
                          className={
                            validation.touched.user_type &&
                            validation.errors.user_type
                              ? "is-invalid"
                              : ""
                          }
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user_type}
                        >
                          <option value="" disabled>
                            Select User Type
                          </option>
                          <option
                            selected={
                              userType.ADMIN === validation.values.user_type
                            }
                            key={userType.ADMIN}
                            value={userType.ADMIN}
                          >
                            Admin
                          </option>
                          <option
                            selected={
                              userType.FINALANALYSIS ===
                              validation.values.user_type
                            }
                            key={userType.FINALANALYSIS}
                            value={userType.FINALANALYSIS}
                          >
                            Final Analisys
                          </option>
                          <option
                            selected={
                              userType.STOCKCHECK ===
                              validation.values.user_type
                            }
                            key={userType.STOCKCHECK}
                            value={userType.STOCKCHECK}
                          >
                            Stock Check
                          </option>
                          <option
                            selected={
                              userType.INITIALANALYSIS ===
                              validation.values.user_type
                            }
                            key={userType.INITIALANALYSIS}
                            value={userType.INITIALANALYSIS}
                          >
                            Initial Analisys
                          </option>
                          <option
                            selected={
                              userType.BC === validation.values.user_type
                            }
                            key={userType.BC}
                            value={userType.BC}
                          >
                            User
                          </option>
                        </Input>
                        {validation.touched.user_type &&
                        validation.errors.user_type ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user_type}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            name="user_free"
                            type="checkbox"
                            className="form-check-input"
                            onChange={(e) => {
                              validation.handleChange(e);
                              if (!e.target.checked) {
                                validation.setFieldValue('user_freeType', null);
                              }
                            }}
                            onBlur={validation.handleBlur}
                            checked={validation.values.user_free || false}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bpaSwitch"
                          >
                            This User is Free
                          </label>
                        </div>
                      </div>
                      {validation.values.user_free && (
                        <div className="mb-3">
                          <Label className="form-label">Free User Type</Label>
                          <Input
                            name="freeTypeDropdown"
                            type="select"
                            className={
                              validation.errors.user_freeType
                                ? "is-invalid"
                                : ""
                            }
                            onChange={(e) => {
                              setFreeTypeDropdown(e.target.value);
                              if (e.target.value !== 'Other') {
                                setFreeTypeCustomReason('');
                              }
                              validation.setFieldError('user_freeType', '');
                            }}
                            value={freeTypeDropdown}
                          >
                            <option value="">Select Free User Type</option>
                            <option value="BPA">BPA</option>
                            <option value="Team">Team</option>
                            <option value="Trial">Trial</option>
                            <option value="Other">Other</option>
                          </Input>
                          {validation.errors.user_freeType ? (
                            <FormFeedback type="invalid">
                              {validation.errors.user_freeType}
                            </FormFeedback>
                          ) : null}
                        </div>
                      )}
                      {validation.values.user_free &&
                        freeTypeDropdown === 'Other' && (
                          <div className="mb-3">
                            <Input
                              name="freeTypeCustomReason"
                              type="text"
                              placeholder="Add a note on why they get free access"
                              maxLength={FREE_TYPE_CUSTOM_REASON_MAX_LENGTH}
                              className={
                                validation.errors.user_freeType
                                  ? "is-invalid"
                                  : ""
                              }
                              onChange={(e) => {
                                setFreeTypeCustomReason(e.target.value.slice(0, FREE_TYPE_CUSTOM_REASON_MAX_LENGTH));
                                validation.setFieldError('user_freeType', '');
                              }}
                              value={freeTypeCustomReason}
                            />
                            {validation.errors.user_freeType ? (
                              <FormFeedback type="invalid">
                                {validation.errors.user_freeType}
                              </FormFeedback>
                            ) : null}
                          </div>
                        )}
                      {isTrial && (
                        <div className="mb-3">
                          <Label className="form-label">Trial End Date</Label>
                          <Input
                            name="user_billingCycleEnd"
                            type="date"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.user_billingCycleEnd || ""}
                          />
                        </div>
                      )}
                      <div className="mb-3">
                        <div className="form-check form-switch form-switch-md align-items-center">
                          <input
                            name="user_showWarning"
                            type="checkbox"
                            className="form-check-input"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            checked={
                              validation.values.user_showWarning || false
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="showWarningSwitch"
                          >
                            Show Warning to User
                          </label>
                        </div>
                      </div>
                      {validation.values.user_showWarning && (
                        <div className="mb-3">
                          <Label className="form-label">Warning Message</Label>
                          <Input
                            name="user_warningText"
                            type="textarea"
                            className={
                              validation.touched.user_warningText &&
                              validation.errors.user_warningText
                                ? "is-invalid"
                                : ""
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.user_warningText || ""}
                          />
                          {validation.touched.user_warningText &&
                          validation.errors.user_warningText ? (
                            <FormFeedback type="invalid">
                              {validation.errors.user_warningText}
                            </FormFeedback>
                          ) : null}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <div className="text-end">
                        <button
                          onClick={onClose}
                          type="button"
                          className="btn btn-danger waves-effect waves-light me-1"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success waves-effect waves-light "
                        >
                          Save
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default EditUser;
